import { postConciliation } from "modules/finops/conciliaciones/services/conciliacionService";
import { useState, useEffect } from "react";

// ==================== HELPERS ====================
const safeNumber = (num) => {
  const n = parseFloat(num);
  return isNaN(n) ? 0 : Math.round(n); // Redondear y evitar decimales
};

const normalizeText = (text) => {
  if (!text) return "";

  // Reemplazos comunes cuando el encoding viene mal
  return text
    .replace(/�/g, "ó") // Corrige Conciliaci�n → Conciliación
    .replace(/Ã¡/g, "á")
    .replace(/Ã©/g, "é")
    .replace(/Ã­/g, "í")
    .replace(/Ã³/g, "ó")
    .replace(/Ãº/g, "ú")
    .replace(/Ã±/g, "ñ")
    .replace(/Ã/g, "Ñ")
    .normalize("NFC");
};

export const useTablaChipichape = () => {
  // ==================== ESTADOS ====================
  const [conciliaciones, setConciliaciones] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedConciliacion, setSelectedConciliacion] = useState("");
  const [selectedEstado, setSelectedEstado] = useState("");
  const [datosCompletos, setDatosCompletos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);

  // ==================== EFECTOS ====================
  useEffect(() => {
    cargarDatosFiltros();
  }, []);

  // ==================== FUNCIONES DE CARGA ====================
  const cargarDatosFiltros = async () => {
    try {
      setLoading(true);
      const response = await postConciliation("chipichape");

      if (response && response.data) {
        const normalizados = response.data.map((item) => ({
          ...item,
          llave_conciliacion: normalizeText(item.llave_conciliacion),
          novedades_llave: normalizeText(item.novedades_llave),
        }));

        setDatosCompletos(normalizados);
        setDatosFiltrados(normalizados);

        // Conciliaciones únicas
        const llavesUnicas = [...new Set(normalizados.map((item) => item.llave_conciliacion))];
        const conciliacionesOptions = llavesUnicas.map((llave, index) => ({
          id: index + 1,
          nombre: llave,
        }));
        setConciliaciones(conciliacionesOptions);

        // Estados únicos
        const novedadesUnicas = [...new Set(normalizados.map((item) => item.novedades_llave))];
        const estadosOptions = novedadesUnicas.map((novedad, index) => ({
          id: index + 1,
          nombre: novedad,
        }));
        setEstados(estadosOptions);
      }
    } catch (error) {
      console.error("Error al cargar datos de filtros:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== FUNCIONES DE PROCESAMIENTO ====================
  const procesarDatosConciliaciones = (data) => {
    const conciliacionesMap = {};

    data.forEach((item) => {
      const llave = item.llave_conciliacion;

      if (!conciliacionesMap[llave]) {
        conciliacionesMap[llave] = {
          llave_conciliacion: llave,
          cantidadTX: 0,
          valorTransacciones: 0,
          comision: 0,
          ivaComision: 0,
          retefuente: 0,
          reteica: 0,
          aPagar: 0,
          transacciones: [],
          tieneNovedad: false,
        };
      }

      conciliacionesMap[llave].cantidadTX += 1;
      conciliacionesMap[llave].valorTransacciones += safeNumber(item.valor_servicio);
      conciliacionesMap[llave].comision += safeNumber(item.comision);
      conciliacionesMap[llave].ivaComision += safeNumber(item.iva_comision);
      conciliacionesMap[llave].retefuente += safeNumber(item.retefuente);
      conciliacionesMap[llave].reteica += safeNumber(item.reteica);

      conciliacionesMap[llave].aPagar =
        conciliacionesMap[llave].valorTransacciones -
        conciliacionesMap[llave].comision -
        conciliacionesMap[llave].ivaComision -
        conciliacionesMap[llave].retefuente -
        conciliacionesMap[llave].reteica;

      conciliacionesMap[llave].transacciones.push(item);

      if (item.novedades_llave && item.novedades_llave.trim().toUpperCase() !== "SIN NOVEDAD") {
        conciliacionesMap[llave].tieneNovedad = true;
      }
    });

    return Object.values(conciliacionesMap).map((conciliacion, index) => ({
      id: index + 1,
      conciliacion: {
        name: conciliacion.llave_conciliacion,
        referencia: `TX-${conciliacion.cantidadTX}`,
      },
      estadoConciliacion: conciliacion.tieneNovedad ? "Con novedad" : "Sin novedad",
      cantidadTX: conciliacion.cantidadTX,
      valorTransacciones: conciliacion.valorTransacciones,
      comision: conciliacion.comision,
      ivaComision: conciliacion.ivaComision,
      retefuente: conciliacion.retefuente,
      reteica: conciliacion.reteica,
      aPagar: conciliacion.aPagar,
    }));
  };

  const calcularEstadisticas = (conciliaciones) => {
    let totalPagar = 0;
    let totalTX = 0;
    let totalValorTransacciones = 0;
    let conNovedad = 0;
    let sinNovedad = 0;

    conciliaciones.forEach((conciliacion) => {
      totalPagar += safeNumber(conciliacion.aPagar);
      totalTX += conciliacion.cantidadTX;
      totalValorTransacciones += safeNumber(conciliacion.valorTransacciones);

      if (conciliacion.estadoConciliacion === "Con novedad") {
        conNovedad++;
      } else {
        sinNovedad++;
      }
    });

    return {
      totalPagarComercio: totalPagar,
      totalTransacciones: totalTX,
      totalValorTransacciones,
      conciliacionesConNovedad: conNovedad,
      conciliacionesSinNovedad: sinNovedad,
    };
  };

  const procesarCobroComision = (data) => {
    const comisionMap = {};

    data.forEach((item) => {
      const llave = item.llave_cobro_comision;
      if (!llave) return;

      if (!comisionMap[llave]) {
        comisionMap[llave] = {
          llave_cobro_comision: llave,
          valorServicio: 0,
          comision: 0,
          ivaComision: 0,
          retefuente: 0,
          reteica: 0,
          reteiva: 0,
        };
      }

      comisionMap[llave].valorServicio += safeNumber(item.valor_servicio);
      comisionMap[llave].comision += safeNumber(item.comision);
      comisionMap[llave].ivaComision += safeNumber(item.iva_comision);
      comisionMap[llave].retefuente += safeNumber(item.retefuente);
      comisionMap[llave].reteica += safeNumber(item.reteica);
      comisionMap[llave].reteiva += safeNumber(item.reteiva);
    });

    return Object.values(comisionMap);
  };

  const procesarConciliacionesConNovedad = (data) => {
    const novedadMap = {};

    data.forEach((item) => {
      if (
        item.novedades_llave === "Parking no procesado" &&
        item.estado_conciliacion === "CON NOVEDAD" &&
        item.estado_conciliacion_disponible !== "Conciliación no disponible"
      ) {
        const llave = item.llave_conciliacion;

        if (!novedadMap[llave]) {
          novedadMap[llave] = {
            llave_conciliacion: llave,
            total: 0,
          };
        }

        novedadMap[llave].total += 1;
      }
    });

    return Object.values(novedadMap);
  };

  // ==================== DATOS PROCESADOS ====================
  const datosProcesados = procesarDatosConciliaciones(datosFiltrados);
  const stats = calcularEstadisticas(datosProcesados);
  const datosComision = procesarCobroComision(datosCompletos);
  const datosNovedad = procesarConciliacionesConNovedad(datosFiltrados);

  const rowsNovedad = datosNovedad.map((i) => ({
    tipoConciliacion: normalizeText(i.llave_conciliacion),
    parkingNoProcesado: i.total,
  }));

  const rowsComision = datosComision.map((item) => ({
    llave_cobro_comision: item.llave_cobro_comision,
    valorServicio: `$${safeNumber(item.valorServicio).toLocaleString("es-CO")}`,
    comision: `$${safeNumber(item.comision).toLocaleString("es-CO")}`,
    ivaComision: `$${safeNumber(item.ivaComision).toLocaleString("es-CO")}`,
    retefuente: `$${safeNumber(item.retefuente).toLocaleString("es-CO")}`,
    reteica: `$${safeNumber(item.reteica).toLocaleString("es-CO")}`,
  }));

  const rowsDetalle = datosFiltrados.map((item) => ({
    fechaIngreso: item.fecha_ingreso ? item.fecha_ingreso.split("T")[0] : "-",
    horaIngreso: item.fecha_ingreso ? item.fecha_ingreso.split("T")[1].replace("Z", "") : "-",
    fechaSalida: item.fecha_salida ? item.fecha_salida.split("T")[0] : "-",
    horaSalida: item.fecha_salida ? item.fecha_salida.split("T")[1].replace("Z", "") : "-",
    tiempo: item.tiempo_parqueo || "-",
    placa: item.placa || "-",
    idtransaction: item.idtransaction || "-",
    subtotal: `$${safeNumber(item.subtotal).toLocaleString("es-CO")}`,
    valorDescuento: `$${safeNumber(item.valor_descuento).toLocaleString("es-CO")}`,
    valorImpuesto: `$${safeNumber(item.valor_impuesto).toLocaleString("es-CO")}`,
    valorServicio: `$${safeNumber(item.valor_servicio).toLocaleString("es-CO")}`,
    valorPagado: `$${safeNumber(item.valor_pagado).toLocaleString("es-CO")}`,
    factura: item.factura || "-",
    cufe: item.cufe_factura || "-",
    fechaTransaccion: item.fecha_transaccion || "-",
  }));

  const rows = datosProcesados.map((conciliacion) => ({
    conciliacion: {
      name: normalizeText(conciliacion.conciliacion.name),
      referencia: conciliacion.conciliacion.referencia,
      estado: conciliacion.estadoConciliacion,
    },
    cantidadTX: conciliacion.cantidadTX,
    valorTransacciones: conciliacion.valorTransacciones,
    comision: conciliacion.comision,
    ivaComision: conciliacion.ivaComision,
    retefuente: conciliacion.retefuente,
    reteica: conciliacion.reteica,
    aPagar: conciliacion.aPagar,
    estadoConciliacion: conciliacion.estadoConciliacion,
  }));

  // ==================== MANEJADORES ====================
  const handleFiltrar = () => {
    let datos = [...datosCompletos];

    if (selectedConciliacion) {
      datos = datos.filter((item) => item.llave_conciliacion === selectedConciliacion);
    }

    if (selectedEstado) {
      datos = datos.filter((item) => item.novedades_llave === selectedEstado);
    }

    setDatosFiltrados(datos);
  };

  const handleLimpiarFiltros = () => {
    setSelectedConciliacion("");
    setSelectedEstado("");
    setDatosFiltrados(datosCompletos);
  };

  // ==================== RETORNO ====================
  return {
    conciliaciones,
    estados,
    selectedConciliacion,
    setSelectedConciliacion,
    selectedEstado,
    setSelectedEstado,
    rowsNovedad,
    loading,
    stats,
    rowsComision,
    rowsDetalle,
    datosNovedad,
    rows,
    handleFiltrar,
    handleLimpiarFiltros,
  };
};
